import { BaseDocument } from "./base";
import { ECountry } from "./data/countries";
import { createPartial, createPartialExcluding, createWithoutUnderscoreFields } from "./helpers/partial";

export enum EmployerStatus {
    ACTIVE,
    INACTIVE,
    PENDING,
    SUSPENDED,
    BANNED,
    SOFTDELETE,
    ARCHIVED,
    LOCKED,
}

export enum TypeOfBusiness {
    OTHER,
    B2B,
    B2B2C,
    B2C,
    D2C,
    MARKETPLACE,
    SAAS,
}

export enum EmployerRole {
    ADMIN,
    USER,
}

export enum EmployerType {
    OTHER,
    COMPANY,
    INDIVIDUAL,
    AGENCY,
    NON_PROFIT,
}

export enum StartupStage {
    NONE,
    IDEA_STAGE,
    PRESEED,
    SEED,
    SERIES_A,
    SERIES_B,
    SERIES_C,
    GROWH
}

export enum NumberEmployees {
    EMPLOYEES_0 = "0",
    EMPLOYEES_1_TO_10 = "1 - 10",
    EMPLOYEES_11_TO_30 = "11 - 30",
    EMPLOYEES_31_TO_50 = "31 - 50",
    EMPLOYEES_51_TO_100 = "51 - 100",
    EMPLOYEES_101_TO_200 = "101 - 200",
    EMPLOYEES_201_TO_500 = "201 - 500",
    EMPLOYEES_501_TO_1000 = "501 - 1000",
    EMPLOYEES_1001_TO_5000 = "1001 - 5000",
    EMPLOYEES_5001_TO_10000 = "5001 - 10000",
    EMPLOYEES_10001_PLUS = "10001+",
}

export enum RevenueRange {
    NO_INFO = "Wish not to say",
    REVENUE_0 = "Pre-Revenue",
    REVENUE_1_TO_10000 = "$1 - $10K",
    REVENUE_10001_TO_50000 = "$10K - $50K",
    REVENUE_50001_TO_100000 = "$50K - $100K",
    REVENUE_100001_TO_200001_TO_500000 = "$100K - $500K",
    REVENUE_500001_TO_1000000 = "$500K - $1M",
    REVENUE_1000001_TO_5000000 = "$1M - $5M",
    REVENUE_5000001_TO_10000000 = "$5M - $10M",
    REVENUE_10000001_TO_100000000 = "$10M-100M",
    REVENUE_100000001_PLUS = "$100M+",
}



export interface Employer extends BaseDocument {
    _techMetadata: {
        firebaseUid: string,
    } | null,
    _currentStatus: {
        status: EmployerStatus,
        date: Date,
        descriptionHeader: string,
        detailedDescription: string,
    },
    _statusHistory: {
        status: EmployerStatus,
        date: Date,
        descriptionHeader: string,
        detailedDescription: string,
    }[],
    _activityHistory: any[],
    _referralCode: string,
    _referredBy: string,
    _referredUsersApproved: string[],
    _referredUsersPending: string[],
    _payoutMethods: any[],
    _preferredPaymentMethod: string,
    _emailVerified: boolean,
    _emailVerfifiedAt: Date | null,
    _phoneVerified: boolean,
    _phoneVerifiedAt: Date | null,
    _failedLoginAttempts: number,
    _roles: EmployerRole[],
    _preferences: {
        language: string,
        darkMode: boolean,
        emailNotifications: boolean,
        phoneNotifications: boolean,
    }
    _employerWhiteList: string[],
    _employerBlackList: string[],
    _freelancerWhiteList: string[],
    _freelancerBlackList: string[],
    _walletId: string,

    _individualFreeRoadmaps: number,
    _hasUnlimitedRoadmaps: boolean,
    _usedFreeRoadmaps: number,
    _usedPaidRoadmaps: number,
    _usedUnlimitedRoadmaps: number,

    _individualPmHires: number,
    _hasUnlimitedPmHires: boolean,
    _usedPmHires: number,

    lastActivityAt: Date | null,
    lastLoginAt: Date | null,
    lastLogoutAt: Date | null,

    streetAddress: string,
    streetAddress2: string | null,
    city: string,
    state: string,
    zipCode: string,
    country: ECountry | null,

    timezone: string | null,
    phone: string,
    email: string,
    contactEmail: string,
    profilePictureUrl: string,
    titlePictureUrl: string,
    introductionVideoUrl: string,
    employerName: string,
    employerType: EmployerType,
    bio: string,
    shortBio: string,
    numberEmployees: string,
    revenueRange: string,
    startupStage: StartupStage,
    typesOfBusiness: TypeOfBusiness[],

    industries: string[],
    languages: string[],

    socialLinks: {
        linkedin: string,
        facebook: string,
        x: string,
        instagram: string,
        github: string,
        youtube: string,
        stackoverflow: string,
        pinterest: string,
        tiktok: string,
        reddit: string,
        behance: string,
        dribbble: string,
        websites: string[],
    }

    projects: string[],

    reviewsTotal: 0,
    reviewsNumber: 0,
    reviews: {
        createdAt: Date,
        reviewerId: string,
        reviewerType: string,
        totalRating: number,
        detailedRatings: {
            clarityProjectScope: number,
            communication: number,
            feedback: number,
            professionalism: number,
            respect: number,
            understanding: number,
            projectManagement: number,
        }
    }[],

}


export const createDefaultEmployer = (): Employer => ({
    _techMetadata: null,
    _currentStatus: {
        status: EmployerStatus.PENDING,
        date: new Date(),
        descriptionHeader: '',
        detailedDescription: '',
    },
    _statusHistory: [{
        status: EmployerStatus.PENDING,
        date: new Date(),
        descriptionHeader: '',
        detailedDescription: '',
    }],
    _activityHistory: [],
    _referralCode: '',
    _referredBy: '',
    _referredUsersApproved: [],
    _referredUsersPending: [],
    _payoutMethods: [],
    _preferredPaymentMethod: '',
    _emailVerified: false,
    _emailVerfifiedAt: null,
    _phoneVerified: false,
    _phoneVerifiedAt: null,
    _failedLoginAttempts: 0,
    _roles: [EmployerRole.USER],
    _preferences: {
        language: 'en',
        darkMode: false,
        emailNotifications: true,
        phoneNotifications: false,
    },
    _employerWhiteList: [],
    _employerBlackList: [],
    _freelancerWhiteList: [],
    _freelancerBlackList: [],
    _walletId: '',

    _individualFreeRoadmaps: 0,
    _hasUnlimitedRoadmaps: false,
    _usedFreeRoadmaps: 0,
    _usedPaidRoadmaps: 0,
    _usedUnlimitedRoadmaps: 0,

    _individualPmHires: 0,
    _hasUnlimitedPmHires: false,
    _usedPmHires: 0,

    lastActivityAt: null,
    lastLoginAt: null,
    lastLogoutAt: null,

    streetAddress: '',
    streetAddress2: null,
    city: '',
    state: '',
    zipCode: '',
    country: null,

    timezone: null,
    phone: '',
    email: '',
    contactEmail: '',
    profilePictureUrl: '',
    titlePictureUrl: '',
    introductionVideoUrl: '',
    employerName: '',
    employerType: EmployerType.COMPANY,
    bio: '',
    shortBio: '',
    numberEmployees: '',
    revenueRange: '',
    startupStage: StartupStage.NONE,
    typesOfBusiness: [],

    industries: [],
    languages: [],
    socialLinks: {
        linkedin: '',
        facebook: '',
        x: '',
        instagram: '',
        github: '',
        youtube: '',
        stackoverflow: '',
        pinterest: '',
        tiktok: '',
        reddit: '',
        behance: '',
        dribbble: '',
        websites: [],
    },
    projects: [],
    reviewsTotal: 0,
    reviewsNumber: 0,
    reviews: [],
});

export function employerMeFull(employer: IEmployer): IEmployer {
    const excludeFields = [
        '_techMetadata',
    ]
    return createPartialExcluding(employer, excludeFields);
}

export function employerMeBrief(employer: IEmployer): IEmployer {
    const fields = [
        '_id',
        'email',
        'employerName',
        'employerType',
        'profilePictureUrl',
        'titlePictureUrl',
        'bio',
        'shortBio',
        'reviewsTotal',
        'reviewsNumber',
    ]
    return createPartial(employer, fields);
}

export function employerFull(employer: IEmployer): IEmployer {
    return employer;
}

export function employerBrief(employer: IEmployer): IEmployer {
    const fields = [
        '_id',
        'email',
        'employerName',
        'employerType',
        'profilePictureUrl',
        'titlePictureUrl',
        'bio',
        'shortBio',
        'reviewsTotal',
        'reviewsNumber',
    ]
    return createPartial(employer, fields);
}

export interface IEmployer extends Partial<Employer> { };